import { escapeHtml } from '@sola/utils/escapeHtml.ts';

/**
 * Fetch an image URL and return it as a base64 data URL.
 * Falls back to the original URL if the fetch fails.
 */
const fetchImageAsDataUrl = async (src: string): Promise<string> => {
  try {
    const resp = await fetch(src);
    const blob = await resp.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.addEventListener('error', reject);
      reader.readAsDataURL(blob);
    });
  } catch {
    return src;
  }
};

export interface PrintableCardProps {
  bgDataUrl: string;
  personalNote: string;
  giftLabel: string;
  giftMessage: string;
  titleColor: string;
}

/** Build a self-contained print-ready HTML string for the honor card. */
export const buildPrintableHtml = ({
  bgDataUrl,
  personalNote,
  giftLabel,
  giftMessage,
  titleColor,
}: PrintableCardProps): string => `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>Honor Card - Print</title>
<style>
  @media print {
    @page { size: letter portrait; margin: 0; }
    body { width: 8.5in; height: 11in; margin: 0; }
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    width: 8.5in;
    height: 11in;
    margin: 0;
    font-family: Helvetica, Arial, sans-serif;
    overflow: scroll;
  }
  .card-page {
    width: 8.5in;
    height: 11in;
    display: grid;
    grid-template: 1fr / 1fr;
  }
  .card-page > * {
    grid-area: 1 / 1 / -1 / -1;
  }
  .bg {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: 100%;
    height: 100%;
  }
  .panel {
    padding: 0.5in;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
  /* Top-left: Inside message (rotated 180°) */
  .panel--inside-message { transform: rotate(180deg); text-align: left; align-items: flex-start; padding-top: 1.2in; }
  .panel--inside-message .personal-note { font-size: 11pt; white-space: pre-line; line-height: 1.6; }
  /* Top-right: Inside gift message (rotated 180°) */
  .panel--inside-gift { transform: rotate(180deg); font-size: 9pt; text-align: center; align-items: center; line-height: 1.5; max-width: 4.25in; padding-left: 0.9in; padding-right: 0.9in; }
  .panel--inside-gift .gift-title { font-size: 11pt; font-weight: bold; text-align: center; margin-bottom: 0.2in; width: 100%; }
  /* Print button */
  .print-btn {
    position: fixed;
    top: 12px;
    right: 12px;
    z-index: 10;
    padding: 10px 20px;
    font-size: 14px;
    font-family: Helvetica, Arial, sans-serif;
    background: #1a73e8;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  .print-btn:hover { background: #1557b0; }
  @media print {
    .print-btn { display: none; }
  }
</style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">Print</button>
  <div class="card-page">
  <img class="bg" src="${escapeHtml(bgDataUrl)}" alt=""/>
  <div class="content">
    <div class="panel panel--inside-message">
      ${personalNote ? `<p class="personal-note">${escapeHtml(personalNote)}</p>` : ''}
    </div>
    <div class="panel panel--inside-gift">
      ${giftLabel ? `<p class="gift-title" style="color: ${escapeHtml(titleColor)}">${escapeHtml(giftLabel)}</p><p>${escapeHtml(giftMessage)}</p>` : ''}
    </div>
  </div>
  </div>
</body>
</html>`;

export interface HandleDownloadPdfParams {
  coverCategory: string;
  coverNumber: number;
  personalNote: string;
  giftLabel: string;
  giftMessage: string;
  titleColor: string;
  getBackgroundSrc: (category: string, number: number) => string;
}

/**
 * Open a print-ready HTML view of the card in a new tab.
 * Fetches the background image as a data URL before opening the tab
 * to avoid CORS issues in the standalone page.
 */
export const openPrintableCard = async ({
  coverCategory,
  coverNumber,
  personalNote,
  giftLabel,
  giftMessage,
  titleColor,
  getBackgroundSrc,
}: HandleDownloadPdfParams): Promise<void> => {
  const bgSrc = getBackgroundSrc(coverCategory, coverNumber);
  const bgDataUrl = await fetchImageAsDataUrl(bgSrc);

  const html = buildPrintableHtml({
    bgDataUrl,
    personalNote,
    giftLabel,
    giftMessage,
    titleColor,
  });

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = globalThis.open(url, '_blank');

  if (!win) {
    // Popup was blocked — fall back to navigating the current tab.
    globalThis.location.href = url;
  }

  // Revoke after a short delay to allow the new tab to load the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
